import { Badge, Box, Text } from 'braid-design-system';
import { compute } from 'compute-scroll-into-view';
import { useCombobox } from 'downshift';
import {
  type ComponentProps,
  Fragment,
  useEffect,
  useRef,
  useState,
} from 'react';

import { SearchResult } from './SearchResult.tsx';

import type { SearchResultItem } from '#search/types.ts';
import { SearchSpinner } from './SearchSpinner.tsx';
import * as styles from './SiteSearch.css';

const useIsMac = () => {
  const [isMac, setIsMac] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    setIsMac(Boolean(globalThis.navigator?.platform?.match('Mac')));
  }, []);

  return isMac;
};

export const SearchField = ({
  inline,
  onChooseResult,
  onForceOpen,
  onSearchTermChange,
  results,
  searchTerm,
  isLoading,
  isError,
}: {
  inline?: boolean;
  onChooseResult: (result: SearchResultItem) => void;
  onForceOpen?: () => void;
  onSearchTermChange: (term: string) => void;
  results: SearchResultItem[];
  searchTerm: string;
  isLoading: boolean;
  isError: boolean;
}) => {
  const ref = useRef<HTMLInputElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const isMac = useIsMac();

  useEffect(() => {
    if (isMac === undefined) {
      return;
    }

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (isMac ? e.metaKey : e.ctrlKey)) {
        e.preventDefault();
        e.stopPropagation();

        onForceOpen?.();
        setTimeout(() => {
          ref.current?.focus();
        }, 1);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onForceOpen, isMac]);

  const { getMenuProps, getInputProps, highlightedIndex, getItemProps } =
    useCombobox({
      id: 'site-search',
      isOpen,
      onInputValueChange: ({ inputValue }) => {
        onSearchTermChange(inputValue);
      },
      items: results,
      stateReducer: (state, { changes, type }) => {
        if (type === useCombobox.stateChangeTypes.InputKeyDownEscape) {
          if (state.inputValue === '') {
            removeFocus(ref);
          }

          onSearchTermChange('');

          return {
            ...changes,
            inputValue: '',
          };
        }

        if (!changes.selectedItem) {
          return changes;
        }

        onChooseResult(changes.selectedItem);
        removeFocus(ref);

        return {
          ...changes,
          inputValue: '',
          selectedItem: null,
        };
      },
      scrollIntoView: (node, menuNode) => {
        if (!node) {
          return;
        }

        const actions = compute(node, {
          boundary: menuNode,
          block: 'center',
          scrollMode: 'if-needed',
        });

        actions.forEach(({ el, top, left }) => {
          el.scrollTop = top;
          el.scrollLeft = left;
        });
      },
    });

  const inputProps = getInputProps<ComponentProps<typeof Box>>({
    ref,
    onKeyDown: (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
      }
    },
  });

  return (
    <Box width="full" position="relative">
      <Box width="full">
        <Box
          // We can't use a braid <TextField /> because it doesn't accept all props downshift wants, notably onKeyDown
          component="input"
          placeholder="Search site"
          className={styles.input}
          {...inputProps}
          onFocus={(e) => {
            setIsOpen(true);
            inputProps.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsOpen(false);
            inputProps.onBlur?.(e);
          }}
        />
        <Box className={[styles.icon, styles.iconLeft]} background="surface">
          <Text>
            <SearchSpinner isLoading={isLoading} />
          </Text>
        </Box>
        {!inline && isMac !== undefined && !isOpen && (
          <Box className={[styles.icon, styles.iconRight]} background="surface">
            <Text>
              <Badge tone="neutral">{isMac ? '⌘' : 'Ctrl + '}K</Badge>
            </Text>
          </Box>
        )}
      </Box>

      {isLoading && results.length === 0 ? null : (
        <Box
          component="ul"
          display={isOpen && searchTerm ? 'block' : 'none'}
          background="surface"
          className={styles.menu}
          {...getMenuProps()}
        >
          {isError && (
            <Box padding="small" component="li">
              <Text tone="critical">
                Something went wrong loading results. Please refresh and try
                again.
              </Text>
            </Box>
          )}
          {results.length === 0 && !isError && (
            <Box padding="small" component="li">
              <Text tone="secondary">No results found</Text>
            </Box>
          )}
          {results.map((result, index) => (
            <Fragment key={result.path}>
              <Box
                component="li"
                background={
                  highlightedIndex === index ? 'formAccentSoft' : undefined
                }
                className={styles.menuItem}
                {...getItemProps({ item: result, index })}
              >
                <SearchResult result={result} />
              </Box>
            </Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

const removeFocus = (ref: React.RefObject<HTMLInputElement | null>) => {
  ref.current?.blur();

  /**
   * Chrome seems to have a bug where it focuses the body on blur ⬆
   * But then command up/down doesn't scroll the page, despite option up/down, spacebar, etc, all scrolling.
   * This seems to work. I hate it.
   */
  const hiddenFocus = document.createElement('input');
  hiddenFocus.style.position = 'absolute';
  hiddenFocus.style.left = '-9999px';
  hiddenFocus.setAttribute('tabindex', '-1');

  document.body.appendChild(hiddenFocus);

  hiddenFocus.focus({ preventScroll: true });

  document.body.removeChild(hiddenFocus);
  document.body.focus({ preventScroll: true });
};
